import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from 'lucide-react';
import { cn } from "../lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-secondary text-primary-foreground text-bg-500 hover:bg-secondary",
        secondary: "border-transparent bg-secondary text-primary-foreground hover:bg-secondary/90",
        destructive: "border-transparent bg-error-500 text-primary-foreground hover:bg-error-500/90",
        outline: "border-secondary text-text-200 disabled:text-text-100 disabled:border-bg-200",
        success: "border-transparent bg-success-500 text-primary-foreground hover:bg-success-500/90",
        warning: "border-transparent bg-warning-500 text-primary-foreground hover:bg-warning-500/90",
        info: "border-transparent bg-cyan-500 text-primary-foreground hover:bg-cyan-500/90",
        red: "border-transparent bg-error-500 text-primary-foreground hover:bg-error-500/90",
        error: "border-transparent bg-error-500 text-primary-foreground hover:bg-error-500/90",
        primary: "border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        alert: "border-transparent bg-warning-500 text-primary-foreground hover:bg-warning-500/90",
        pending: "border-transparent bg-pending-500 text-primary-foreground hover:bg-pending-500/90",
        disable: "border-transparent bg-bg-200 text-primary-foreground hover:bg-bg-200/80",
        disableOutline: "text-text-100 border border-bg-200",
        gradient: "bg-gradient-to-r from-primary to-secondary text-primary-foreground hover:opacity-90",
        soft: "border-transparent bg-primary/10 text-primary-foreground hover:bg-primary/20",
        softError: "border-transparent bg-error-500/10 text-primary-foreground hover:bg-error-500/20",
        softSuccess: "border-transparent bg-success-500/10 text-success-500 hover:bg-success-500/20",
      },
      size: {
        default: "h-6 px-2.5 py-0.5 text-xs",
        sm: "h-5 px-2 py-0 text-xs",
        lg: "h-7 px-3 py-1 text-sm",
        xl: "h-8 px-4 py-1.5 text-base",
      },
      shape: {
        default: "rounded-full",
        square: "rounded-md",
        pill: "rounded-full px-4",
        circle: "rounded-full p-0 flex items-center justify-center",
        number: "rounded-full size-5 p-0 flex items-center justify-center",
        dot: "rounded-full size-2 p-0",
        tag: "rounded-sm px-2 py-0.5",
      },
      position: {
        default: "relative",
        "top-right": "absolute -top-2 -right-2",
        "top-left": "absolute -top-2 -left-2",
        "bottom-right": "absolute -bottom-0 -right-0",
        "bottom-left": "absolute -bottom-0 -left-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
      position: "default",
    },
  }
)

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content'>,
    VariantProps<typeof badgeVariants> {
  removable?: boolean;
  onRemove?: () => void;
  badgeContent?: React.ReactNode;
  containerClassName?: string;
  avatar?: string;
  avatarAlt?: string;
}

function Badge({
  className,
  variant,
  size,
  shape,
  position,
  removable = false,
  onRemove,
  badgeContent,
  containerClassName,
  avatar,
  avatarAlt = "User avatar",
  children,
  ...props
}: BadgeProps) {
  // If we have a container and position is not default, wrap the badge in a container
  if (position && position !== "default") {
    return (
      <div className={cn("relative inline-block items-center", containerClassName)}>
        {children}
        <div
          data-slot="badge"
          className={cn(badgeVariants({ variant, size, shape, position }), className)}
          {...props}
        >
          <span className="inline-flex items-center justify-center gap-1 leading-2">
            {avatar && (
              <img 
                src={avatar} 
                alt={avatarAlt} 
                className="w-4 h-4 rounded-full object-cover"
              />
            )}
            {badgeContent || null}
            {removable && (
              <button
                type="button"
                className="cursor-pointer rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.();
                }}
                aria-hidden="true"
              >
                <X className="text-error-500"/>
              </button>
            )}
          </span>
        </div>
      </div>
    )
  }
  
  // Regular badge without positioning
  return (
    <div
      data-slot="badge"
      className={cn(badgeVariants({ variant, size, shape, position }), className)}
      {...props}
    >
      <div className="inline-flex items-center justify-center gap-1 leading-2">
        {avatar && (
          <img 
            src={avatar} 
            alt={avatarAlt} 
            className="w-4 h-4 rounded-full object-cover"
          />
        )}
        {badgeContent || children}
        {removable && (
          <button
            type="button"
            className="ml-1 -mr-1 h-4 w-4 rounded-full cursor-pointer inline-flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3.5 w-3.5"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
            <span className="sr-only">Remove badge</span>
          </button>
        )}
      </div>
    </div>
  )
}

export { Badge, badgeVariants }
